/**
 * Springs for the glass system, with Apple's parameters: `response` is the
 * period of the undamped oscillation in seconds, `damping` is the damping
 * ratio (1 = critical). Overshoot is exp(-pi * d / sqrt(1 - d^2)), so the
 * presets below stay at or under 3%:
 *
 *   MORPH  response 0.45, damping 0.86   0.5% overshoot  (shapes, the droplet)
 *   LEAD   response 0.28, damping 0.86   leading edge of a moving selection
 *   TRAIL  response 0.42, damping 0.86   trailing edge of a moving selection
 *   PRESS  response 0.25, damping 0.78   2.0% overshoot  (press swell)
 *
 * Integration is semi-implicit Euler with fixed sub-steps (at most 1/480 s),
 * which is stable for these stiffnesses at any frame rate. All springs share
 * one requestAnimationFrame loop that stops when every spring is at rest.
 * With prefers-reduced-motion, every spring jumps straight to its target.
 */

export const SPRING = {
  MORPH: { response: 0.45, damping: 0.86 },
  LEAD: { response: 0.28, damping: 0.86 },
  TRAIL: { response: 0.42, damping: 0.86 },
  PRESS: { response: 0.25, damping: 0.78 },
} as const;

/** Peak overshoot of a unit step, as a fraction (0.02 = 2%). */
export function springOvershoot(damping: number): number {
  if (damping >= 1) return 0;
  return Math.exp((-Math.PI * damping) / Math.sqrt(1 - damping * damping));
}

/* ---------- reduced motion / transparency (live media queries) ---------- */

function liveQuery(query: string) {
  let value = false;
  let mql: MediaQueryList | null = null;
  const listeners = new Set<() => void>();
  function ensure() {
    if (mql || typeof window === "undefined" || !window.matchMedia) return;
    try {
      mql = window.matchMedia(query);
      value = mql.matches;
      mql.addEventListener("change", (e) => {
        value = e.matches;
        listeners.forEach((l) => l());
      });
    } catch {
      mql = null;
    }
  }
  return {
    get(): boolean {
      ensure();
      return value;
    },
    subscribe(cb: () => void): () => void {
      ensure();
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

export const reducedMotion = liveQuery("(prefers-reduced-motion: reduce)");
export const reducedTransparency = liveQuery("(prefers-reduced-transparency: reduce)");
export const coarsePointer = liveQuery("(hover: none), (pointer: coarse)");

/* ---------- the spring ---------- */

const active = new Set<Spring>();
let raf = 0;
let last = 0;

function tick(now: number) {
  const dt = Math.min(0.05, Math.max(0.001, (now - last) / 1000));
  last = now;
  const fired = new Set<() => void>();
  for (const s of active) {
    s.step(dt);
    if (s.atRest()) {
      s.x = s.target;
      s.v = 0;
      active.delete(s);
    }
    if (s.onUpdate) fired.add(s.onUpdate);
  }
  fired.forEach((f) => f());
  raf = active.size ? requestAnimationFrame(tick) : 0;
}

function kick() {
  if (!raf && typeof requestAnimationFrame !== "undefined") {
    last = performance.now();
    raf = requestAnimationFrame(tick);
  }
}

export interface SpringOptions {
  response?: number;
  damping?: number;
  /** Distance (in the spring's own units) under which it counts as at rest. */
  epsilon?: number;
}

export class Spring {
  x: number;
  v = 0;
  target: number;
  response: number;
  damping: number;
  epsilon: number;
  /** Called once per frame while the spring moves (and once when it is set). */
  onUpdate: (() => void) | null = null;

  constructor(x: number, { response = SPRING.MORPH.response, damping = SPRING.MORPH.damping, epsilon = 0.01 }: SpringOptions = {}) {
    this.x = x;
    this.target = x;
    this.response = response;
    this.damping = damping;
    this.epsilon = epsilon;
  }

  /** Animate toward `target`, optionally with a new response for this move. */
  to(target: number, response?: number): void {
    if (response != null) this.response = response;
    this.target = target;
    if (reducedMotion.get()) {
      this.set(target);
      return;
    }
    active.add(this);
    kick();
  }

  /** Jump to `x` with no motion. */
  set(x: number): void {
    this.x = this.target = x;
    this.v = 0;
    active.delete(this);
    this.onUpdate?.();
  }

  stop(): void {
    active.delete(this);
  }

  atRest(): boolean {
    return Math.abs(this.x - this.target) < this.epsilon && Math.abs(this.v) < this.epsilon * 10;
  }

  step(dt: number): void {
    const w = (2 * Math.PI) / this.response;
    const k = w * w;
    const c = 2 * this.damping * w;
    const n = Math.max(1, Math.ceil(dt * 480));
    const h = dt / n;
    for (let i = 0; i < n; i++) {
      const a = -k * (this.x - this.target) - c * this.v;
      this.v += a * h;
      this.x += this.v * h;
    }
  }
}

/**
 * Rubber band: past [min, max] the value keeps moving but with growing
 * resistance, approaching `limit` units beyond the edge.
 */
export function rubberBand(x: number, min: number, max: number, limit = 24): number {
  if (x < min) {
    const o = min - x;
    return min - (1 - 1 / ((o * 0.55) / limit + 1)) * limit;
  }
  if (x > max) {
    const o = x - max;
    return max + (1 - 1 / ((o * 0.55) / limit + 1)) * limit;
  }
  return x;
}
